import {Trans} from '@lingui/macro';
import React, {memo} from 'react';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ToolbarButton} from '../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {ComposerPanelId, ViewerPanelId} from '../../../../store/panels/panelStateBase';
import {FlexSpacer} from '../../../common/flex';
import {BaseComposerToolbar} from './baseComposerToolbar';
import {AnswersToolbarButtonProps} from './buttons/answersToolbarButton';
import {AnswersToolbarButtonContainer} from './buttons/answersToolbarButtonContainer';
import {AttachmentsToolbarButtonPublicProps} from './buttons/attachmentsToolbarButton';
import {AttachmentsToolbarButtonContainer} from './buttons/attachmentsToolbarButtonContainer';
import {DeleteToolbarButton, DeleteToolbarButtonProps} from './buttons/deleteToolbarButton';
import {DoneToolbarButton, DoneToolbarButtonProps} from './buttons/doneToolbarButton';
import {EmojiToolbarButton, EmojiToolbarButtonProps} from './buttons/emojiToolbarButton';
import {GifToolbarButton, GifToolbarButtonProps} from './buttons/gifToolbarButton';
import {SchedulingToolbarButton, SchedulingToolbarButtonProps} from './buttons/schedulingToolbarButton';
import {
  SendToolbarButtonContainer,
  SendToolbarButtonContainerProps,
} from './buttons/sendToolbarButtonContainer';
import {TagsToolbarButton, TagsToolbarButtonProps} from './buttons/tagsToolbarButton';
import {DraftSaveIndicator, DraftSaveStatusesEnum} from './draftSaveIndicator';

/*
 * Props.
 */

export interface FullComposerToolbarProps
  extends EmojiToolbarButtonProps,
    GifToolbarButtonProps,
    AttachmentsToolbarButtonPublicProps,
    AnswersToolbarButtonProps,
    DeleteToolbarButtonProps,
    SendToolbarButtonContainerProps,
    DoneToolbarButtonProps,
    SchedulingToolbarButtonProps,
    TagsToolbarButtonProps {
  panelId: ComposerPanelId | ViewerPanelId;
  isUserVisitor?: boolean;
  supportsFormatting?: boolean;
  supportsScheduling?: boolean;
  isFormattingToolbarOpen?: boolean;
  toggleIsFormattingToolbarOpen?: Handler;
  conversation: ConversationModel | null;
  draftSaveStatus: DraftSaveStatusesEnum;
  isDraftSaving: boolean;
}

/*
 * Component.
 */

export const FullComposerToolbar = memo<FullComposerToolbarProps>((props) => (
  <UserThemeConsumer>
    {(theme) => (
      <BaseComposerToolbar>
        {props.supportsFormatting && (
          <ToolbarButton
            iconName="formatting"
            onClick={props.toggleIsFormattingToolbarOpen}
            label={<Trans id="composer-formatting">Formatting options</Trans>}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
            hasHover={props.isFormattingToolbarOpen}
          />
        )}
        <EmojiToolbarButton {...props} />
        {maybeRenderGifsButton(props)}
        <AttachmentsToolbarButtonContainer {...props} />
        {!props.isUserVisitor && <AnswersToolbarButtonContainer {...props} />}
        {maybeRenderSchedulingButton(props)}
        {maybeRenderTagsButton(props)}
        {!props.isUserVisitor && <DeleteToolbarButton {...props} />}
        <FlexSpacer />
        <DraftSaveIndicator status={props.draftSaveStatus} isSaving={props.isDraftSaving} />
        {renderRightActions(props)}
      </BaseComposerToolbar>
    )}
  </UserThemeConsumer>
));

function maybeRenderTagsButton(props: FullComposerToolbarProps) {
  const {message} = props;

  // If we're replying to an existing message, the tag button is redundant with the topbar.
  if (message.inReplyToId) {
    return null;
  }

  return <TagsToolbarButton {...props} />;
}

function maybeRenderSchedulingButton(props: FullComposerToolbarProps) {
  if (!props.supportsScheduling) {
    return null;
  }

  return <SchedulingToolbarButton {...props} />;
}

function renderRightActions(props: FullComposerToolbarProps) {
  if (props.isUserVisitor) {
    return <DoneToolbarButton {...props} />;
  }

  return <SendToolbarButtonContainer {...props} />;
}

function maybeRenderGifsButton(props: FullComposerToolbarProps) {
  const {shouldDisplayGifs} = props;

  if (!shouldDisplayGifs) {
    return null;
  }

  return <GifToolbarButton {...props} />;
}
